import { initReveal } from '../common/reveal';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const splitCsv = (value: string | null): string[] =>
    (value ?? '')
        .split(',')
        .map(item => item.trim().toLowerCase())
        .filter(Boolean);

function initCraftHero(): void {
    const hero = document.querySelector<HTMLElement>('[data-craft-hero]');
    if (!hero) {
        return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const code = hero.querySelector<HTMLElement>('[data-hero-code]');
    const dashboard = hero.querySelector<HTMLElement>('[data-hero-dashboard]');
    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-reveal]'));

    if (!prefersReducedMotion) {
        gsap.set(revealItems, { autoAlpha: 0, y: 24 });
        if (code) {
            gsap.set(code, { autoAlpha: 0, x: -72, y: 28, rotateY: 24, rotateX: 7 });
        }

        if (dashboard) {
            gsap.set(dashboard, { autoAlpha: 0, x: 72, y: 32, rotateY: -24, rotateX: 7 });
        }

        let settled = false;
        const settle = (): void => {
            if (settled) {
                return;
            }

            settled = true;
            gsap.set(revealItems, { autoAlpha: 1, y: 0, clearProps: 'visibility' });
        };

        const timeline = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: settle });

        if (code) {
            timeline.to(code, { autoAlpha: 1, x: 0, y: 0, rotateY: 0, rotateX: 0, duration: 1.05 }, 0.02);
        }

        if (dashboard) {
            timeline.to(dashboard, { autoAlpha: 1, x: 0, y: 0, rotateY: 0, rotateX: 0, duration: 1.05 }, 0.12);
        }

        if (revealItems.length > 0) {
            timeline.to(revealItems, { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.08 }, 0.28);
        }

        window.setTimeout(settle, 1800);
    }

    if (prefersReducedMotion || (!code && !dashboard)) {
        return;
    }

    hero.addEventListener('mousemove', event => {
        const rect = hero.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;

        if (code) {
            gsap.to(code, {
                x: x * 18,
                y: y * 14,
                rotateX: -y * 4,
                rotateY: 14 + x * 7,
                duration: 0.5,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        if (dashboard) {
            gsap.to(dashboard, {
                x: x * -18,
                y: y * 14,
                rotateX: -y * 4,
                rotateY: -14 + x * 7,
                duration: 0.5,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }
    });

    hero.addEventListener('mouseleave', () => {
        if (code) {
            gsap.to(code, {
                x: 0,
                y: 0,
                rotateX: 0,
                rotateY: 14,
                duration: 0.8,
                ease: 'power3.out',
                overwrite: 'auto'
            });
        }

        if (dashboard) {
            gsap.to(dashboard, {
                x: 0,
                y: 0,
                rotateX: 0,
                rotateY: -14,
                duration: 0.8,
                ease: 'power3.out',
                overwrite: 'auto'
            });
        }
    });
}

function initCompaniesScroll(): void {
    const story = document.querySelector<HTMLElement>('[data-company-story]');
    const steps = Array.from(story?.querySelectorAll<HTMLElement>('[data-company-step]') ?? []);
    if (!story || steps.length === 0) {
        return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const intro = document.querySelector<HTMLElement>('.companies-section .company-intro');
    const introSignals = Array.from(intro?.querySelectorAll<HTMLElement>('.company-intro__system span') ?? []);
    const sticky = story.querySelector<HTMLElement>('.company-scroll-story__sticky');
    const stage = story.querySelector<HTMLElement>('.company-stage');
    const progress = story.querySelector<HTMLElement>('[data-company-progress]');
    const stageIndex = story.querySelector<HTMLElement>('[data-company-stage-index]');
    const stageLogo = story.querySelector<HTMLImageElement>('[data-company-stage-logo]');
    const stageKicker = story.querySelector<HTMLElement>('[data-company-stage-kicker]');
    const stageName = story.querySelector<HTMLElement>('[data-company-stage-name]');
    const stageSummary = story.querySelector<HTMLElement>('[data-company-stage-summary]');
    let activeIndex = -1;

    const animateIntro = (): void => {
        if (prefersReducedMotion || !intro) {
            return;
        }

        gsap.to(intro, {
            '--intro-gradient-stop': '38%',
            '--intro-grid-opacity': 0.48,
            ease: 'none',
            scrollTrigger: {
                trigger: intro,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
            }
        });

        if (introSignals.length > 0) {
            gsap.fromTo(
                introSignals,
                { autoAlpha: 0, y: 28, scale: 0.98 },
                {
                    autoAlpha: 1,
                    y: 0,
                    scale: 1,
                    stagger: 0.08,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: intro,
                        start: 'top 62%',
                        end: 'center 42%',
                        scrub: 0.5
                    }
                }
            );
        }
    };

    const animateStoryAtmosphere = (): void => {
        if (prefersReducedMotion || !sticky) {
            return;
        }

        gsap.fromTo(
            sticky,
            { '--story-grid-opacity': 0.34, '--company-flare-x': '74%' },
            {
                '--story-grid-opacity': 0.92,
                '--company-flare-x': '48%',
                ease: 'none',
                scrollTrigger: {
                    trigger: story,
                    start: 'top bottom',
                    end: 'top top',
                    scrub: true
                }
            }
        );
    };

    const setActive = (index: number): void => {
        const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
        const nextStep = steps[nextIndex];
        if (!nextStep) {
            return;
        }

        if (activeIndex === nextIndex) {
            return;
        }

        const direction = activeIndex === -1 || nextIndex >= activeIndex ? 1 : -1;
        activeIndex = nextIndex;
        story.style.setProperty('--company-accent', nextStep.dataset.companyAccent ?? '#cba160');
        story.style.setProperty('--company-accent-rgb', nextStep.dataset.companyAccentRgb ?? '203, 161, 96');

        steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === nextIndex));

        if (stageIndex) stageIndex.textContent = nextStep.dataset.companyLabel ?? `0${nextIndex + 1}`;
        if (stageKicker) stageKicker.textContent = nextStep.dataset.companyKicker ?? '';
        if (stageName) stageName.textContent = nextStep.dataset.companyName ?? '';
        if (stageSummary) stageSummary.textContent = nextStep.dataset.companySummary ?? '';
        if (stageLogo && nextStep.dataset.companyLogo) {
            stageLogo.src = nextStep.dataset.companyLogo;
        }

        if (!prefersReducedMotion) {
            const stageItems = [stageLogo, stageKicker, stageName, stageSummary].filter((item): item is HTMLElement => Boolean(item));
            const chapterItems = Array.from(nextStep.querySelectorAll<HTMLElement>('.company-chapter__brand, .company-chapter__context, .company-chapter__contribution'));

            gsap.killTweensOf(stageItems);
            gsap.fromTo(
                stageItems,
                { autoAlpha: 0, y: direction * 22, filter: 'blur(10px)' },
                {
                    autoAlpha: 1,
                    y: 0,
                    filter: 'blur(0px)',
                    duration: 0.46,
                    stagger: 0.045,
                    ease: 'power3.out',
                    overwrite: 'auto',
                    clearProps: 'filter,opacity,visibility'
                }
            );

            gsap.killTweensOf(chapterItems);
            gsap.fromTo(
                chapterItems,
                { autoAlpha: 0, y: 30, filter: 'blur(12px)' },
                {
                    autoAlpha: 1,
                    y: 0,
                    filter: 'blur(0px)',
                    duration: 0.62,
                    stagger: 0.055,
                    ease: 'power3.out',
                    overwrite: 'auto',
                    clearProps: 'filter,opacity,visibility,transform'
                }
            );

            if (stage) {
                gsap.fromTo(
                    stage,
                    { scale: 0.985, rotateZ: direction * 0.35 },
                    { scale: 1, rotateZ: 0, duration: 0.58, ease: 'power3.out', overwrite: 'auto', clearProps: 'transform' }
                );
            }

            if (sticky) {
                gsap.fromTo(
                    sticky,
                    { '--company-flare-x': direction > 0 ? '78%' : '22%' },
                    { '--company-flare-x': '50%', duration: 0.8, ease: 'power2.out', overwrite: 'auto' }
                );
            }
        }
    };

    if (prefersReducedMotion) {
        setActive(0);
        steps.forEach(step => step.classList.add('is-active'));
        if (progress) progress.style.transform = 'scaleY(1)';
        return;
    }

    animateIntro();
    animateStoryAtmosphere();
    setActive(0);

    steps.forEach((step, index) => {
        ScrollTrigger.create({
            trigger: step,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => setActive(index),
            onEnterBack: () => setActive(index)
        });
    });

    if (progress) {
        ScrollTrigger.create({
            trigger: story,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            onUpdate: self => {
                progress.style.transform = `scaleY(${Math.max(0.04, self.progress)})`;
                const index = Math.min(steps.length - 1, Math.round(self.progress * (steps.length - 1)));
                if (index !== activeIndex) {
                    setActive(index);
                }
            }
        });
    }

    window.addEventListener('resize', () => ScrollTrigger.refresh(), { passive: true });
}

function initPortfolioFilters(): void {
    const cardsContainer = document.getElementById('prjCards');
    if (!cardsContainer) {
        return;
    }

    const searchInput = document.getElementById('prjSearch') as HTMLInputElement | null;
    const companySelect = document.getElementById('prjCompany') as HTMLSelectElement | null;
    const competencySelect = document.getElementById('prjCompetency') as HTMLSelectElement | null;
    const industrySelect = document.getElementById('prjIndustry') as HTMLSelectElement | null;
    const sortSelect = document.getElementById('prjSort') as HTMLSelectElement | null;
    const clearBtn = document.getElementById('prjClearFilters') as HTMLButtonElement | null;
    const cards = Array.from(cardsContainer.querySelectorAll<HTMLElement>('.prj-card'));
    const emptyText = cardsContainer.getAttribute('data-empty-text') ?? 'No projects found.';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let filterRun = 0;

    const getSelectValue = (select: HTMLSelectElement | null): string =>
        (select?.value ?? 'all').trim().toLowerCase();

    const ensureEmptyState = (visibleCount: number): void => {
        let emptyState = cardsContainer.querySelector<HTMLElement>('.empty-state-msg');
        const shouldAnimate = !emptyState;

        if (visibleCount === 0 && !emptyState) {
            emptyState = document.createElement('p');
            emptyState.className = 'muted empty-state empty-state-msg';
            emptyState.textContent = emptyText;
            cardsContainer.appendChild(emptyState);
        }

        if (visibleCount > 0) {
            emptyState?.remove();
        }

        if (visibleCount === 0 && emptyState && shouldAnimate && !prefersReducedMotion) {
            gsap.fromTo(
                emptyState,
                { autoAlpha: 0, y: 14 },
                { autoAlpha: 1, y: 0, duration: 0.28, ease: 'power2.out', overwrite: 'auto' }
            );
        }
    };

    const sortCards = (): void => {
        const activeSort = getSelectValue(sortSelect);
        const sortedCards = [...cards].sort((a, b) => {
            if (activeSort === 'alpha') {
                return (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '', undefined, { sensitivity: 'base' });
            }

            if (activeSort === 'impact') {
                return Number(b.dataset.impact ?? 0) - Number(a.dataset.impact ?? 0);
            }

            return Number(b.dataset.recent ?? 0) - Number(a.dataset.recent ?? 0);
        });

        sortedCards.forEach(card => cardsContainer.appendChild(card));
    };

    const getRenderedCards = (): HTMLElement[] =>
        cards.filter(card => card.style.display !== 'none' && !card.classList.contains('is-hidden'));

    const lockGridHeight = (): void => {
        const height = cardsContainer.getBoundingClientRect().height;
        cardsContainer.style.minHeight = `${Math.max(1, Math.round(height))}px`;
    };

    const unlockGridHeight = (): void => {
        cardsContainer.style.minHeight = '';
    };

    const getFilteredCards = (): HTMLElement[] => {
        const searchQuery = (searchInput?.value ?? '').trim().toLowerCase();
        const activeCompany = getSelectValue(companySelect);
        const activeCompetency = getSelectValue(competencySelect);
        const activeIndustry = getSelectValue(industrySelect);
        const visibleCards: HTMLElement[] = [];

        cards.forEach(card => {
            const company = (card.dataset.company ?? '').toLowerCase();
            const industry = (card.dataset.category ?? '').toLowerCase();
            const competencies = splitCsv(card.dataset.competencies ?? '');
            const searchText = (card.dataset.search ?? '').toLowerCase();

            const matchesCompany = activeCompany === 'all' || company === activeCompany;
            const matchesCompetency = activeCompetency === 'all' || competencies.includes(activeCompetency);
            const matchesIndustry = activeIndustry === 'all' || industry === activeIndustry;
            const matchesSearch = !searchQuery || searchText.includes(searchQuery);
            const isVisible = matchesCompany && matchesCompetency && matchesIndustry && matchesSearch;

            if (isVisible) {
                visibleCards.push(card);
            }
        });

        return visibleCards;
    };

    const applyVisibility = (visibleCards: HTMLElement[]): void => {
        const visibleSet = new Set(visibleCards);

        cards.forEach(card => {
            const isVisible = visibleSet.has(card);
            card.classList.toggle('is-hidden', !isVisible);
            card.style.display = isVisible ? '' : 'none';
        });
    };

    const filterAndSort = (animate = true): void => {
        const runId = ++filterRun;
        const visibleCards = getFilteredCards();
        const visibleSet = new Set(visibleCards);
        const currentlyRenderedCards = getRenderedCards();
        const firstRects = new Map(currentlyRenderedCards.map(card => [card, card.getBoundingClientRect()]));
        const exitingCards = currentlyRenderedCards.filter(card => !visibleSet.has(card));

        const finish = (): void => {
            if (runId !== filterRun) {
                return;
            }

            applyVisibility(visibleCards);
            sortCards();
            ensureEmptyState(visibleCards.length);

            if (prefersReducedMotion || !animate || visibleCards.length === 0) {
                cardsContainer.classList.remove('is-filtering');
                unlockGridHeight();
                return;
            }

            const sortedVisibleCards = Array
                .from(cardsContainer.querySelectorAll<HTMLElement>('.prj-card'))
                .filter(card => visibleSet.has(card));
            const movingCards = sortedVisibleCards.filter(card => firstRects.has(card));
            const enteringCards = sortedVisibleCards.filter(card => !firstRects.has(card));
            const animatedCards = [...movingCards, ...enteringCards];

            movingCards.forEach(card => {
                const firstRect = firstRects.get(card);
                const lastRect = card.getBoundingClientRect();
                if (!firstRect) {
                    return;
                }

                gsap.set(card, {
                    x: firstRect.left - lastRect.left,
                    y: firstRect.top - lastRect.top,
                    scale: 0.995,
                    autoAlpha: 1,
                    filter: 'blur(0px)'
                });
            });

            gsap.set(enteringCards, {
                autoAlpha: 0,
                y: 28,
                scale: 0.975,
                filter: 'blur(10px)'
            });

            if (animatedCards.length === 0) {
                cardsContainer.classList.remove('is-filtering');
                unlockGridHeight();
                return;
            }

            gsap.to(animatedCards, {
                x: 0,
                y: 0,
                scale: 1,
                autoAlpha: 1,
                filter: 'blur(0px)',
                duration: 0.46,
                stagger: 0.025,
                ease: 'power3.out',
                overwrite: 'auto',
                clearProps: 'filter,opacity,visibility,transform',
                onComplete: () => {
                    cardsContainer.classList.remove('is-filtering');
                    unlockGridHeight();
                }
            });
        };

        if (prefersReducedMotion || !animate) {
            gsap.killTweensOf(cards);
            finish();
            return;
        }

        cardsContainer.classList.add('is-filtering');
        lockGridHeight();
        gsap.killTweensOf(cards);

        if (exitingCards.length === 0) {
            finish();
            return;
        }

        gsap.to(exitingCards, {
            autoAlpha: 0,
            y: 14,
            scale: 0.985,
            filter: 'blur(6px)',
            duration: 0.16,
            stagger: 0.012,
            ease: 'power2.in',
            overwrite: 'auto',
            onComplete: finish
        });
    };

    searchInput?.addEventListener('input', () => filterAndSort());
    companySelect?.addEventListener('change', () => filterAndSort());
    competencySelect?.addEventListener('change', () => filterAndSort());
    industrySelect?.addEventListener('change', () => filterAndSort());
    sortSelect?.addEventListener('change', () => filterAndSort());

    clearBtn?.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        if (companySelect) companySelect.value = 'all';
        if (competencySelect) competencySelect.value = 'all';
        if (industrySelect) industrySelect.value = 'all';
        if (sortSelect) sortSelect.value = 'recent';
        filterAndSort();
    });

    filterAndSort(false);
}

export function initCraftPage(): void {
    const craftPage = document.getElementById('craftPage');
    if (!craftPage) {
        return;
    }

    initReveal({ selector: '#craftPage [data-reveal]', threshold: 0.16 });
    if (window.location.hash) {
        window.setTimeout(() => {
            craftPage.querySelectorAll<HTMLElement>('[data-reveal]').forEach(element => {
                element.classList.add('is-visible');
            });
        }, 350);
    }

    initCraftHero();
    initCompaniesScroll();
    initPortfolioFilters();

    if (window.location.hash.length > 1) {
        window.setTimeout(() => {
            const target = document.getElementById(window.location.hash.slice(1));
            target?.scrollIntoView({ block: 'start' });
            ScrollTrigger.refresh();
        }, 650);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCraftPage);
} else {
    initCraftPage();
}
